import { format, formatDistanceToNow, isToday, isYesterday, parseISO } from 'date-fns'
import { User } from '@/types'

// Date formatting utilities
export const formatDate = (date: string | Date): string => {
  const parsedDate = typeof date === 'string' ? parseISO(date) : date

  if (isToday(parsedDate)) {
    return `Today at ${format(parsedDate, 'h:mm a')}`
  }

  if (isYesterday(parsedDate)) {
    return `Yesterday at ${format(parsedDate, 'h:mm a')}`
  }

  return format(parsedDate, 'MMM d, h:mm a')
}

// Time formatting for PostgreSQL TIME fields (HH:MM:SS or HH:MM:SS.ffffff)
export const formatTimeString = (timeStr: string | null | undefined): string => {
  if (!timeStr) return ''

  try {
    const [hours, minutes] = timeStr.split(':')
    const hour = parseInt(hours, 10)
    const min = minutes || '00'
    const ampm = hour >= 12 ? 'PM' : 'AM'
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
    return `${displayHour}:${min} ${ampm}`
  } catch (error) {
    console.error('Failed to format time string:', timeStr, error)
    return timeStr
  }
}

export const formatRelativeTime = (date: string | Date): string => {
  const parsedDate = typeof date === 'string' ? parseISO(date) : date
  return formatDistanceToNow(parsedDate, { addSuffix: true })
}

export const formatDateShort = (date: string | Date): string => {
  const parsedDate = typeof date === 'string' ? parseISO(date) : date
  return format(parsedDate, 'MMM d')
}

// Points formatting
export const formatPoints = (points: number): string => {
  if (points >= 1000) {
    return `${(points / 1000).toFixed(1)}k`
  }
  return points.toString()
}

export const formatPointsWithLabel = (points: number): string => {
  const formatted = formatPoints(points)
  return `${formatted} ${points === 1 ? 'point' : 'points'}`
}

// User utilities
export const getUserColor = (user: User): string => {
  return user.color_theme || '#3498db'
}

export const getUserInitials = (user: User): string => {
  return user.display_name
    .split(' ')
    .map(name => name[0])
    .join('')
    .toUpperCase()
    .slice(0, 2)
}

export const getUserLevel = (xp: number): number => {
  // Level calculation: level = floor(sqrt(xp / 100)) + 1
  return Math.floor(Math.sqrt(xp / 100)) + 1
}

export const getXpForLevel = (level: number): number => {
  // XP required for level: xp = (level - 1)^2 * 100
  return Math.pow(level - 1, 2) * 100
}

export const getXpProgress = (currentXp: number, currentLevel: number): {
  currentLevelXp: number
  nextLevelXp: number
  progress: number
} => {
  const currentLevelXp = getXpForLevel(currentLevel)
  const nextLevelXp = getXpForLevel(currentLevel + 1)
  const progressXp = currentXp - currentLevelXp
  const requiredXp = nextLevelXp - currentLevelXp
  const progress = Math.min((progressXp / requiredXp) * 100, 100)
  
  return {
    currentLevelXp: progressXp,
    nextLevelXp: requiredXp,
    progress
  }
}

// Chore utilities
export const getDifficultyColor = (difficulty: string): string => {
  switch (difficulty) {
    case 'easy':
      return 'bg-green-100 text-green-800'
    case 'medium':
      return 'bg-yellow-100 text-yellow-800'
    case 'hard':
      return 'bg-orange-100 text-orange-800'
    case 'expert':
      return 'bg-red-100 text-red-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

export const getCategoryColor = (category: string): string => {
  switch (category) {
    case 'routine':
      return 'bg-blue-100 text-blue-800'
    case 'open':
      return 'bg-green-100 text-green-800'
    case 'premium':
      return 'bg-purple-100 text-purple-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

export const getStatusColor = (status: string): string => {
  switch (status) {
    case 'pending':
      return 'bg-gray-100 text-gray-800'
    case 'claimed':
      return 'bg-blue-100 text-blue-800'
    case 'in_progress':
      return 'bg-yellow-100 text-yellow-800'
    case 'completed':
      return 'bg-orange-100 text-orange-800'
    case 'verified':
      return 'bg-green-100 text-green-800'
    case 'expired':
      return 'bg-red-100 text-red-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

export const getEstimatedTime = (minutes?: number): string => {
  if (!minutes) return 'Unknown'
  
  if (minutes < 60) {
    return `${minutes} min`
  }
  
  const hours = Math.floor(minutes / 60)
  const remainingMinutes = minutes % 60
  
  if (remainingMinutes === 0) {
    return `${hours}h`
  }
  
  return `${hours}h ${remainingMinutes}m`
}

// Achievement utilities
export const getRarityColor = (rarity: string): string => {
  switch (rarity) {
    case 'common':
      return 'bg-gray-100 text-gray-800 border-gray-300'
    case 'rare':
      return 'bg-blue-100 text-blue-800 border-blue-300'
    case 'epic':
      return 'bg-purple-100 text-purple-800 border-purple-300'
    case 'legendary':
      return 'bg-yellow-100 text-yellow-800 border-yellow-300'
    default:
      return 'bg-gray-100 text-gray-800 border-gray-300'
  }
}

export const getRarityGlow = (rarity: string): string => {
  switch (rarity) {
    case 'rare':
      return 'shadow-lg shadow-blue-500/25'
    case 'epic':
      return 'shadow-lg shadow-purple-500/25'
    case 'legendary':
      return 'shadow-xl shadow-yellow-500/50 animate-pulse'
    default:
      return ''
  }
}

// Local storage utilities
export const saveToStorage = (key: string, value: any): void => {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch (error) {
    console.error(`Failed to save to localStorage:`, error)
  }
}

export const loadFromStorage = <T>(key: string, defaultValue: T): T => {
  try {
    const item = localStorage.getItem(key)
    return item ? JSON.parse(item) : defaultValue
  } catch (error) {
    console.error(`Failed to load from localStorage:`, error)
    return defaultValue
  }
}

export const removeFromStorage = (key: string): void => {
  try {
    localStorage.removeItem(key)
  } catch (error) {
    console.error(`Failed to remove from localStorage:`, error)
  }
}

// Image utilities
export const getAvatarUrl = (user?: User): string => {
  if (user?.avatar_url) {
    return user.avatar_url
  }
  
  // Generate a deterministic avatar based on username
  const colors = ['#3498db', '#e74c3c', '#2ecc71', '#f1c40f', '#9b59b6', '#e67e22']
  const colorIndex = user ? user.username.charCodeAt(0) % colors.length : 0
  const initials = user ? getUserInitials(user) : '?'
  
  return `data:image/svg+xml,${encodeURIComponent(`
    <svg width="40" height="40" viewBox="0 0 40 40" xmlns="http://www.w3.org/2000/svg">
      <circle cx="20" cy="20" r="20" fill="${colors[colorIndex]}"/>
      <text x="20" y="28" text-anchor="middle" fill="white" font-family="Arial, sans-serif" font-size="16" font-weight="bold">${initials}</text>
    </svg>
  `)}`
}

// File utilities
export const formatFileSize = (bytes: number): string => {
  if (bytes === 0) return '0 B'
  
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(1))} ${sizes[i]}`
}

export const isImageFile = (file: File): boolean => {
  return file.type.startsWith('image/')
}

// Validation utilities
export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return emailRegex.test(email)
}

export const isValidUsername = (username: string): boolean => {
  const usernameRegex = /^[a-zA-Z0-9_]{3,20}$/
  return usernameRegex.test(username)
}

// Animation utilities
export const getRandomConfettiColor = (): string => {
  const colors = ['#ff0000', '#00ff00', '#0000ff', '#ffff00', '#ff00ff', '#00ffff']
  return colors[Math.floor(Math.random() * colors.length)]
}

export const createConfettiPiece = (): HTMLElement => {
  const confetti = document.createElement('div')
  confetti.className = 'absolute w-2 h-2 animate-confetti pointer-events-none'
  confetti.style.backgroundColor = getRandomConfettiColor()
  confetti.style.left = `${Math.random() * 100}%`
  confetti.style.animationDelay = `${Math.random() * 1000}ms`
  return confetti
}

// Error handling utilities

// Message for an error toast: the server's `error`, `detail` or `message` when
// it sent one, otherwise `fallback`. Never axios's "Request failed with status
// code N".
export const getErrorMessage = (error: unknown, fallback = 'Something went wrong'): string => {
  if (typeof error === 'string') {
    return error || fallback
  }

  if (error && typeof error === 'object') {
    const data = (error as { response?: { data?: unknown } }).response?.data
    if (data && typeof data === 'object') {
      for (const key of ['error', 'detail', 'message']) {
        const value = (data as Record<string, unknown>)[key]
        if (typeof value === 'string' && value.trim()) {
          return value
        }
      }
    }
    if ('response' in error || (error as { isAxiosError?: boolean }).isAxiosError) {
      return fallback
    }
    if ('detail' in error && typeof (error as { detail: unknown }).detail === 'string') {
      return (error as { detail: string }).detail
    }
  }

  if (error instanceof Error && error.message) {
    return error.message
  }

  return fallback
}

// Theme utilities
export const getThemeFromUser = (user: User) => ({
  primary: user.color_theme,
  primaryLight: `${user.color_theme}20`,
  primaryDark: adjustColor(user.color_theme, -20),
})

function adjustColor(color: string, amount: number): string {
  const useParse = parseInt(color.slice(1), 16)
  const r = Math.max(0, Math.min(255, (useParse >> 16) + amount))
  const g = Math.max(0, Math.min(255, (useParse >> 8 & 0x00FF) + amount))
  const b = Math.max(0, Math.min(255, (useParse & 0x0000FF) + amount))
  return `#${(0x1000000 + (r << 16) + (g << 8) + b).toString(16).slice(1)}`
}